// Quick-create de negócio — modal pra criar proposta nova com cliente/contato
// (opção de criar empresa/contato inline se não existir).
// Extraído de ComercialPipeline.tsx em 2026-05-23 (frontend wave 3B).
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Input, Select, Modal, Form, Typography, App as AntApp, Radio, DatePicker,
  InputNumber, Row, Col, Upload, Button, Alert, Spin, Space,
} from 'antd';
import { UploadOutlined, RobotOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { Cliente, Contato } from './types';
import { STATUS_LABELS } from './utils';
import { SelectComCriar } from './SelectComCriar';
import { FormCriarCliente } from './FormCriarCliente';
import { FormCriarContato } from './FormCriarContato';
import { usePerms } from '../../hooks/usePerms';

const { Text } = Typography;

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

const MODALIDADES_LICITACAO = [
  'Pregão Eletrônico', 'Pregão Presencial', 'Concorrência - Eletrônica',
  'Concorrência - Presencial', 'Dispensa de Licitação', 'Inexigibilidade',
  'Credenciamento', 'Concurso', 'Diálogo Competitivo',
];

export function FormNovoNegocio({ clientes, onClose, onCriado }: {
  clientes: Cliente[];
  onClose: () => void;
  onCriado: (criado: any) => void;
}) {
  const { message } = AntApp.useApp();
  const nav = useNavigate();
  const [tipo, setTipo] = useState<'privada' | 'licitacao'>('privada');
  const [form, setForm] = useState({
    titulo: '', cliente_id: '', contato_id: '', status: 'prospeccao',
    vendedor_email: '', responsavel: '',
    // REMVAR F1: canal de aquisição + quem trouxe o lead (originação é
    // registrada AQUI, cedo — depois de qualificação ela trava)
    canal_origem: '', originador_email: '',
  });
  const [meuEmailLogado, setMeuEmailLogado] = useState('');
  // REMVAR: indicações abertas da empresa escolhida (deal registration)
  const [sugestoes, setSugestoes] = useState<any[]>([]);
  const [indicacaoAceita, setIndicacaoAceita] = useState<number | null>(null);
  const [formLic, setFormLic] = useState({
    orgao: '', uf: '', cidade: '', modalidade: 'Pregão Eletrônico',
    objeto: '', data_abertura: '', valor_estimado: '',
    numero_edital: '', area_tecnica: '',
  });
  const [editalPdf, setEditalPdf] = useState<File | null>(null);
  const [editalS3Key, setEditalS3Key] = useState<string>('');
  const [extraindo, setExtraindo] = useState(false);
  const [extraidoOk, setExtraidoOk] = useState(false);
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [criandoCliente, setCriandoCliente] = useState<{ termo: string } | null>(null);
  const [criandoContato, setCriandoContato] = useState<{ termo: string } | null>(null);
  const [clientesLocais, setClientesLocais] = useState(clientes);
  // Empresa nova exige `comercial.empresas.criar` no servidor (05/10/2026).
  // Falha ao carregar a matriz não vira "não pode".
  const { pode, erro: permsErro } = usePerms();
  const podeCriarEmpresa = permsErro || pode('comercial.empresas.criar');
  // Licitação manual (e a leitura do edital por IA) tem portão no servidor desde
  // 05/10/2026: comercial.pipeline.criar_negocio_licitacao.
  const podeLicitacao = permsErro || pode('comercial.pipeline.criar_negocio_licitacao');
  // Negócio privado também tem portão desde 05/10/2026 (criar_negocio_privado).
  const podePrivado = permsErro || pode('comercial.pipeline.criar_negocio_privado');
  useEffect(() => { if (!podePrivado && podeLicitacao) setTipo('licitacao'); }, [podePrivado, podeLicitacao]);
  // Quem originou = crédito de comissão: régua de quem gera a venda desde
  // 06/10/2026 (o servidor recusa). Sem ela, a originação fica para a aba
  // Créditos, por quem tem.
  const podeCreditos = permsErro || pode('comercial.negocio_privado.gerar_venda');
  const [vendedores, setVendedores] = useState<Array<{ email: string; nome: string }>>([]);

  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        const meuEmail = ((session.tokens?.idToken?.payload as any)?.email || '').toLowerCase();
        const token = session.tokens?.idToken?.toString() || '';
        const r = await axios.get(`${API_URL}/admin/usuarios`,
          { headers: { Authorization: token } });
        const data = r.data.body ? JSON.parse(r.data.body) : r.data;
        const lista = Array.isArray(data) ? data : [];
        // Vendedor == quem tem acesso ao módulo comercial. O grupo 'vendedor'
        // separado foi descontinuado (era redundante). Filtra usuários ativos
        // que estejam em 'comercial' ou 'admin'.
        const vends = lista
          .filter((u: any) => u.enabled !== false &&
            ((u.groups || []).includes('comercial') || (u.groups || []).includes('admin')))
          .map((u: any) => ({ email: u.email, nome: u.nome || u.email }));
        setVendedores(vends);
        setMeuEmailLogado(meuEmail);
        const meuPerfil = vends.find((v: any) => v.email.toLowerCase() === meuEmail);
        if (meuPerfil) {
          setForm(f => ({ ...f, vendedor_email: meuPerfil.email, responsavel: meuPerfil.nome }));
        }
      } catch {}
    })();
  }, []);

  async function trocarCliente(id: string) {
    setForm(f => ({ ...f, cliente_id: id, contato_id: '' }));
    setContatos([]);
    setSugestoes([]);
    if (!id) return;
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/comercial/clientes/${id}/contatos`,
        { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setContatos(data);
    } catch {}
    // REMVAR: alguém já indicou esta empresa? O casamento é por SUGESTÃO —
    // o sistema mostra, quem lança decide.
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'com_sugerir_indicacoes', cliente_id: parseInt(id) },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setSugestoes(d.sugestoes || []);
    } catch { /* sem sugestão não bloqueia nada */ }
  }

  async function extrairDadosDoPdf() {
    if (!editalPdf) { message.warning('Selecione o PDF do edital primeiro'); return; }
    setExtraindo(true);
    try {
      const token = await getToken();
      const headers = { Authorization: token };
      // 1) Upload temporario
      const r1 = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'upload_url_temp_pdf', filename: editalPdf.name,
          content_type: editalPdf.type || 'application/pdf' },
        { headers });
      const d1 = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
      await axios.put(d1.upload_url, editalPdf, {
        headers: { 'Content-Type': editalPdf.type || 'application/pdf' },
      });
      setEditalS3Key(d1.s3_key);
      // 2) Haiku le PDF e extrai
      const r2 = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'extrair_dados_edital_pdf', s3_key: d1.s3_key },
        { headers });
      const d2 = r2.data.body ? JSON.parse(r2.data.body) : r2.data;
      const dados = d2.dados || {};
      // Preenche o form com o que veio (mantem o que ja foi digitado)
      setFormLic(prev => ({
        orgao: dados.orgao || prev.orgao,
        uf: dados.uf || prev.uf,
        cidade: dados.cidade || prev.cidade,
        modalidade: dados.modalidade || prev.modalidade,
        objeto: dados.objeto || prev.objeto,
        data_abertura: dados.data_abertura || prev.data_abertura,
        valor_estimado: dados.valor_estimado != null ? String(dados.valor_estimado) : prev.valor_estimado,
        numero_edital: dados.numero_edital || prev.numero_edital,
        area_tecnica: dados.area_tecnica || prev.area_tecnica,
      }));
      setExtraidoOk(true);
      message.success('Dados extraídos pela IA. Revise e ajuste.');
    } catch (e: any) {
      message.error('Erro ao extrair: ' + (e.response?.data?.error || e.message));
    } finally { setExtraindo(false); }
  }

  async function salvar() {
    setSalvando(true);
    try {
      const token = await getToken();
      if (tipo === 'licitacao') {
        if (!formLic.orgao.trim()) { message.warning('Órgão obrigatório'); setSalvando(false); return; }
        if (!formLic.objeto.trim()) { message.warning('Objeto obrigatório'); setSalvando(false); return; }
        const res = await axios.post(`${API_URL}/comercial/licitacoes`, {
          action: 'criar_licitacao_manual',
          orgao: formLic.orgao,
          uf: formLic.uf || null,
          cidade: formLic.cidade || null,
          modalidade: formLic.modalidade || null,
          objeto: formLic.objeto,
          data_abertura: formLic.data_abertura || null,
          valor_estimado: formLic.valor_estimado ? parseFloat(formLic.valor_estimado) : null,
          numero_edital: formLic.numero_edital || null,
          area_tecnica: formLic.area_tecnica || null,
          pdf_inicial_s3_key: editalS3Key || null,
        }, { headers: { Authorization: token } });
        const data = res.data.body ? JSON.parse(res.data.body) : res.data;
        // 2026-09-02 (Camada 1, item 1.8): a licitação manual NÃO nasce mais
        // com proposta. Ela entra na fila da triagem como as do PNCP e da
        // ConLicitação, e a proposta só existe depois que uma pessoa decide —
        // antes, o robô decidia por ela e descartar deixava proposta órfã no
        // funil. Portanto NÃO há `proposta_id` nesta resposta: quem lê isso
        // aqui como antes mostrava "proposta #undefined" e mandava o pipeline
        // abrir um id vazio.
        message.success(data.triagem_disparada
          ? 'Licitação criada e enviada para a triagem do Dwight — abrindo Licitações.'
          : 'Licitação criada. Anexe o edital para a análise ler os documentos.');
        // `onCriado` SEM id de propósito: o pai faz `setNegocioAberto(criado.id)`
        // e, sem proposta, isso abriria um negócio que não existe. Aqui só
        // fechamos o modal e recarregamos a lista.
        onCriado({ titulo: data.titulo, origem: 'licitacao', status_interno: data.status });
        nav('/comercial/licitacoes?aba=oportunidades');
        return;
      }
      // Tipo privado
      if (!form.titulo.trim()) { message.warning('Título obrigatório'); setSalvando(false); return; }
      const res = await axios.post(`${API_URL}/comercial/propostas`, {
        titulo: form.titulo,
        cliente_id: form.cliente_id ? parseInt(form.cliente_id) : null,
        contato_id: form.contato_id ? parseInt(form.contato_id) : null,
        status: form.status,
        responsavel: form.responsavel || null,
        vendedor_email: form.vendedor_email || null,
        // REMVAR F1: autoria (o backend prefere o claims do Cognito quando a
        // rota passa o event; isto cobre o fallback) + canal de aquisição
        criado_por: meuEmailLogado || null,
        canal_origem: form.canal_origem || null,
      }, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      // Originação. Dois caminhos, nesta ordem de confiança:
      //  (a) indicação aceita — o indicador carimbou ANTES do negócio existir;
      //      vincular credita 100% a ele e fecha o ciclo (avisa quem indicou).
      //  (b) escolha manual — fallback pra quando não houve indicação registrada.
      // Best-effort: falha não desfaz o negócio criado.
      if (indicacaoAceita && data?.id) {
        try {
          await axios.post(`${API_URL}/comercial/propostas-geradas`, {
            action: 'com_vincular_indicacao',
            indicacao_id: indicacaoAceita, proposta_id: data.id,
          }, { headers: { Authorization: token } });
        } catch (e: any) {
          message.warning('Negócio criado, mas a indicação não foi vinculada: '
            + (e.response?.data?.error || e.message), 8);
        }
      } else if (form.originador_email && data?.id) {
        try {
          await axios.post(`${API_URL}/comercial/propostas-geradas`, {
            action: 'com_definir_creditos_papel', proposta_id: data.id,
            papel: 'originacao',
            itens: [{ usuario_email: form.originador_email, peso: 100 }],
          }, { headers: { Authorization: token } });
        } catch (e: any) {
          message.warning('Negócio criado, mas a originação não foi registrada: '
            + (e.response?.data?.error || e.message), 8);
        }
      }
      message.success('Negócio criado.');
      onCriado(data);
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.response?.data?.erro || e.message));
    } finally { setSalvando(false); }
  }

  return (
    <>
      <Modal open onCancel={onClose} onOk={salvar} confirmLoading={salvando}
        title="Novo Negócio" okText="Criar e abrir" cancelText="Cancelar" width={620} destroyOnClose>
        <Form layout="vertical" size="small">
          <Form.Item style={{ marginBottom: 12 }}>
            <Radio.Group value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <Radio.Button value="privada" disabled={!podePrivado}>Negócio privado</Radio.Button>
              <Radio.Button value="licitacao" disabled={!podeLicitacao}>Licitação (manual)</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {tipo === 'licitacao' ? (
            <>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
                Use quando souber da licitação por outro canal (jornal, indicação) — cria oportunidade + card no pipeline.
              </Text>

              <Alert type="info" showIcon style={{ marginBottom: 12 }}
                message={
                  <div>
                    <Text strong style={{ fontSize: 12 }}>Auto-preencher pelo edital</Text>
                    <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Upload beforeUpload={(file) => { setEditalPdf(file); setExtraidoOk(false); return false; }}
                        maxCount={1} onRemove={() => { setEditalPdf(null); setEditalS3Key(''); setExtraidoOk(false); }}
                        accept="application/pdf"
                        fileList={editalPdf ? [{ uid: '1', name: editalPdf.name, status: 'done' } as any] : []}>
                        <Button size="small" icon={<UploadOutlined />}>PDF do edital</Button>
                      </Upload>
                      <Button size="small" type="primary" icon={<RobotOutlined />}
                        onClick={extrairDadosDoPdf} loading={extraindo} disabled={!editalPdf || extraindo}>
                        Extrair dados (Haiku)
                      </Button>
                      {extraidoOk && <Text type="success" style={{ fontSize: 11 }}>✓ Preenchido</Text>}
                    </div>
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      Sobe o PDF temporariamente e a IA preenche os campos abaixo. Revise antes de salvar. PDF fica anexado à oportunidade depois.
                    </Text>
                  </div>
                } />

              <Row gutter={8}>
                <Col span={16}>
                  <Form.Item label="Órgão" required>
                    <Input value={formLic.orgao} autoFocus
                      onChange={e => setFormLic({ ...formLic, orgao: e.target.value })}
                      placeholder="Ex.: Município de São Jerônimo" />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="UF">
                    <Select value={formLic.uf || undefined} allowClear
                      onChange={(v) => setFormLic({ ...formLic, uf: v || '' })}
                      options={UFS.map(u => ({ label: u, value: u }))} />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Cidade">
                    <Input value={formLic.cidade}
                      onChange={e => setFormLic({ ...formLic, cidade: e.target.value })} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item label="Modalidade">
                    <Select value={formLic.modalidade}
                      onChange={(v) => setFormLic({ ...formLic, modalidade: v })}
                      options={MODALIDADES_LICITACAO.map(m => ({ label: m, value: m }))} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Número do edital">
                    <Input value={formLic.numero_edital}
                      onChange={e => setFormLic({ ...formLic, numero_edital: e.target.value })}
                      placeholder="Ex.: PE 042/2026" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Objeto" required>
                <Input.TextArea value={formLic.objeto} rows={2}
                  onChange={e => setFormLic({ ...formLic, objeto: e.target.value })}
                  placeholder="Descrição resumida do que o edital quer..." />
              </Form.Item>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item label="Data de abertura">
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                      value={formLic.data_abertura ? dayjs(formLic.data_abertura) : null}
                      onChange={(d) => setFormLic({ ...formLic, data_abertura: d ? d.format('YYYY-MM-DD') : '' })} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Valor estimado (R$)">
                    <InputNumber style={{ width: '100%' }}
                      value={formLic.valor_estimado ? parseFloat(formLic.valor_estimado) : null}
                      onChange={(v) => setFormLic({ ...formLic, valor_estimado: v ? String(v) : '' })}
                      min={0} step={1000} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Área técnica">
                <Input value={formLic.area_tecnica}
                  onChange={e => setFormLic({ ...formLic, area_tecnica: e.target.value })}
                  placeholder="Ex.: Hidrologia, Topografia (livre)" />
              </Form.Item>
            </>
          ) : (
          <>
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 14 }}>
            Preencha o essencial — você completa os demais dados na página do negócio.
          </Text>
          <Form.Item label="Título" required>
            <Input value={form.titulo} autoFocus onChange={e => setForm({ ...form, titulo: e.target.value })}
              placeholder="Ex.: Levantamento topográfico Usina Y" />
          </Form.Item>
          <Form.Item label="Empresa">
            <SelectComCriar
              valor={form.cliente_id}
              opcoes={clientesLocais.map(c => ({ id: String(c.id), label: c.razao_social }))}
              placeholder={podeCriarEmpresa ? 'Buscar empresa ou criar nova...' : 'Buscar empresa...'}
              onSelecionar={trocarCliente}
              onCriar={podeCriarEmpresa ? termo => setCriandoCliente({ termo }) : undefined} />
          </Form.Item>
          <Form.Item label="Contato">
            <SelectComCriar
              valor={form.contato_id}
              opcoes={contatos.map(c => ({ id: String(c.id), label: `${c.nome}${c.cargo ? ' — ' + c.cargo : ''}` }))}
              placeholder={!form.cliente_id ? 'Selecione a empresa primeiro' :
                contatos.length === 0 ? 'Nenhum contato — clique para criar' : 'Buscar ou criar...'}
              disabled={!form.cliente_id}
              onSelecionar={id => setForm({ ...form, contato_id: id })}
              onCriar={termo => setCriandoContato({ termo })}
              labelCriar="Criar novo contato" />
          </Form.Item>
          <Form.Item label="Vendedor responsável"
            extra="Pré-selecionado: você (criador). Para alterar, escolha outro vendedor.">
            <Select value={form.vendedor_email || undefined}
              onChange={(email) => {
                const v = vendedores.find(x => x.email === email);
                setForm({ ...form, vendedor_email: email, responsavel: v?.nome || '' });
              }}
              placeholder="Sem vendedor atribuído" allowClear
              options={vendedores.map(v => ({ label: v.nome, value: v.email }))} />
          </Form.Item>
          {/* Sem "Fechamento": negócio novo não nasce ganho — o servidor recusa
              (05/10/2026); ganhar é pelo botão "Ganhar", que gera a venda. */}
          <Form.Item label="Etapa do funil">
            <Select value={form.status} onChange={(v) => setForm({ ...form, status: v })}
              showSearch optionFilterProp="label"
              options={Object.entries(STATUS_LABELS).filter(([k]) => k !== 'ganho').map(([k, v]) => ({ label: v, value: k }))} />
          </Form.Item>
          <Form.Item label="Canal de origem"
            extra="Por onde o negócio chegou — alimenta a atribuição comercial.">
            <Select value={form.canal_origem || undefined} allowClear placeholder="— opcional —"
              onChange={(v) => setForm({ ...form, canal_origem: v || '' })}
              options={[
                { value: 'indicacao_interna', label: 'Indicação interna' },
                { value: 'carteira', label: 'Carteira' },
                { value: 'prospeccao_ativa', label: 'Prospecção ativa' },
                { value: 'inbound', label: 'Inbound' },
                { value: 'rede_socio', label: 'Rede do sócio' },
                { value: 'parceiro', label: 'Parceiro' },
              ]} />
          </Form.Item>
          {/* REMVAR: se alguém registrou indicação pra esta empresa ANTES, a
              sugestão aparece aqui. É o caminho forte (prova temporal) e por
              isso vem antes da escolha manual. */}
          {podeCreditos && sugestoes.length > 0 && (
            <Form.Item label="Alguém indicou esta empresa">
              <Alert type="success" showIcon
                message={sugestoes.length === 1
                  ? 'Existe uma indicação registrada para esta empresa'
                  : `Existem ${sugestoes.length} indicações registradas para esta empresa`}
                description={
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    {sugestoes.map(s => (
                      <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="radio" name="indic" checked={indicacaoAceita === s.id}
                          onChange={() => { setIndicacaoAceita(s.id); setForm(f => ({ ...f, originador_email: '' })); }} />
                        <span style={{ fontSize: 12 }}>
                          <strong>{s.indicador_email}</strong> · {new Date(s.criado_em).toLocaleDateString('pt-BR')}
                          {s.assunto ? ` · ${s.assunto}` : ''}
                          {s.casou_por === 'nome' && (
                            <em style={{ color: '#B07500' }}> (casou pelo nome “{s.empresa_nome}”)</em>
                          )}
                        </span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="radio" name="indic" checked={indicacaoAceita === null}
                        onChange={() => setIndicacaoAceita(null)} />
                      <span style={{ fontSize: 12 }}>Nenhuma — este negócio não veio dessas indicações</span>
                    </div>
                  </Space>}
              />
            </Form.Item>
          )}
          {podeCreditos && (
          <Form.Item label="Quem originou (trouxe o lead)?"
            extra={indicacaoAceita
              ? 'Desabilitado: a originação virá da indicação escolhida acima.'
              : 'Use só quando NÃO houve indicação registrada. O caminho recomendado é quem originou registrar a indicação em Comercial → Indicações, antes de o negócio chegar.'}>
            <Select value={form.originador_email || undefined} allowClear
              disabled={!!indicacaoAceita}
              placeholder="— ninguém / procura espontânea —" showSearch optionFilterProp="label"
              onChange={(v) => setForm({ ...form, originador_email: v || '' })}
              options={vendedores.map(v => ({ label: `${v.nome} (${v.email})`, value: v.email }))} />
          </Form.Item>
          )}
          </>
          )}
        </Form>
      </Modal>

      {criandoCliente && (
        <FormCriarCliente termo={criandoCliente.termo}
          onClose={() => setCriandoCliente(null)}
          onCriado={(novo) => {
            setClientesLocais(prev => [...prev, novo]);
            trocarCliente(String(novo.id));
            setCriandoCliente(null);
          }} />
      )}
      {criandoContato && (
        <FormCriarContato termo={criandoContato.termo}
          clienteIdInicial={form.cliente_id}
          clientes={clientesLocais}
          onClose={() => setCriandoContato(null)}
          onCriado={async (novo) => {
            const cid = String(novo.cliente_id || form.cliente_id);
            await trocarCliente(cid);
            setForm(f => ({ ...f, cliente_id: cid, contato_id: String(novo.id) }));
            setCriandoContato(null);
          }} />
      )}
    </>
  );
}
